import { CreditCard } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { useSaveCard, type CardInput, type CardScope, type CreditCard as Card } from '../../credit'
import { toastError } from '../../ui'
import { Footer } from '../AccountForms'
import { BankSelect } from '../banks/BankSelect'
import { Field, FormSection } from '../FormBits'

const RATE = /^(\d{1,2}([.,]\d{1,4})?)?$/

/**
 * Kişisel kart: ad, banka, son 4 hane, limit, kesim/son ödeme günü ve (varsa) karta özel oranlar.
 * Şirket kartı: limit/günler "Kartlar" hesabından düzenlenir; burada yalnız karta özel oranlar tutulur.
 */
export function CardForm({ scope, card, onDone }: { scope: CardScope; card: Card | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSaveCard()
  const personal = scope === 'personal'
  const [v, setV] = useState<CardInput>({
    name: card?.name ?? '',
    bankName: card?.bankName ?? '',
    bankId: card?.bankId ?? '',
    last4: card?.last4 ?? '',
    limit: card?.limit ?? '',
    statementDay: card?.statementDay || 15,
    dueDay: card?.dueDay || 25,
    holderUserId: card?.holderUserId ?? '',
    purchaseRate: card?.purchaseRate ?? '',
    cashAdvanceRate: card?.cashAdvanceRate ?? '',
    lateRate: card?.lateRate ?? '',
  })
  const [touched, setTouched] = useState(false)
  const set = (patch: Partial<CardInput>) => setV((x) => ({ ...x, ...patch }))
  const errors = {
    name: personal && v.name.trim().length < 2 ? t('finance.credit.form.nameRequired') : '',
    last4: v.last4 && !/^\d{4}$/.test(v.last4) ? t('errors.CARD_LAST4_INVALID') : '',
    limit: personal && !(Number(v.limit.replace(',', '.')) > 0) ? t('finance.credit.form.limitRequired') : '',
    days:
      personal && (v.statementDay < 1 || v.statementDay > 31 || v.dueDay < 1 || v.dueDay > 31)
        ? t('finance.credit.form.daysInvalid')
        : '',
    rates: [v.purchaseRate, v.cashAdvanceRate, v.lateRate].some((r) => !RATE.test(r.trim()))
      ? t('finance.accounts.pos.rateInvalid')
      : '',
  }
  const valid = Object.values(errors).every((e) => !e)
  const err = (k: keyof typeof errors) => (touched ? errors[k] || undefined : undefined)

  return (
    <Modal
      open
      size="lg"
      onOpenChange={(open) => !open && onDone()}
      dismissible={!save.isPending}
      icon={<CreditCard />}
      title={t(
        personal
          ? card
            ? 'finance.credit.form.editTitle'
            : 'finance.credit.form.createTitle'
          : 'finance.credit.form.profileTitle',
      )}
      description={t(personal ? 'finance.credit.form.personalHint' : 'finance.credit.form.companyHint')}
      onSubmit={(e) => {
        e.preventDefault()
        setTouched(true)
        if (!valid) return
        save.mutate(
          {
            id: personal ? card?.id || undefined : undefined,
            moneyAccountId: personal ? undefined : card?.moneyAccountId,
            input: { ...v, limit: v.limit.replace(',', '.') },
          },
          {
            onSuccess: () => {
              toast.success(t('finance.credit.form.saved'))
              onDone()
            },
            onError: toastError,
          },
        )
      }}
      footer={<Footer pending={save.isPending} onCancel={onDone} />}
    >
      <div className="space-y-6">
        {personal ? (
          <FormSection title={t('finance.credit.form.sectionCard')}>
            <Field id="cc-name" label={t('finance.credit.form.name')} error={err('name')}>
              <Input id="cc-name" value={v.name} onChange={(e) => set({ name: e.target.value })} />
            </Field>
            <Field id="cc-bank" label={t('finance.accounts.bank')}>
              <BankSelect id="cc-bank" value={v.bankId} onChange={(bankId) => set({ bankId })} />
            </Field>
            <Field
              id="cc-last4"
              label={t('finance.accounts.cardLast4')}
              error={err('last4')}
              hint={t('finance.accounts.form.cardSafety')}
            >
              <Input
                id="cc-last4"
                inputMode="numeric"
                maxLength={4}
                autoComplete="off"
                value={v.last4}
                onChange={(e) => set({ last4: e.target.value })}
              />
            </Field>
            <Field id="cc-limit" label={t('finance.credit.limit')} error={err('limit')}>
              <Input
                id="cc-limit"
                inputMode="decimal"
                value={v.limit}
                onChange={(e) => set({ limit: e.target.value })}
              />
            </Field>
            <Field id="cc-st" label={t('finance.credit.form.statementDay')} error={err('days')}>
              <Input
                id="cc-st"
                inputMode="numeric"
                maxLength={2}
                value={String(v.statementDay)}
                onChange={(e) => set({ statementDay: Number(e.target.value) || 0 })}
              />
            </Field>
            <Field id="cc-due" label={t('finance.credit.form.dueDay')}>
              <Input
                id="cc-due"
                inputMode="numeric"
                maxLength={2}
                value={String(v.dueDay)}
                onChange={(e) => set({ dueDay: Number(e.target.value) || 0 })}
              />
            </Field>
          </FormSection>
        ) : null}
        <FormSection title={t('finance.credit.form.sectionRates')}>
          <p className="text-muted-foreground text-xs sm:col-span-2">{t('finance.credit.form.ratesHint')}</p>
          <Field id="cc-pr" label={t('finance.credit.rates.purchase')} error={err('rates')}>
            <Input
              id="cc-pr"
              inputMode="decimal"
              placeholder={t('finance.credit.form.defaultRate')}
              value={v.purchaseRate}
              onChange={(e) => set({ purchaseRate: e.target.value })}
            />
          </Field>
          <Field id="cc-car" label={t('finance.credit.rates.cashAdvance')}>
            <Input
              id="cc-car"
              inputMode="decimal"
              placeholder={t('finance.credit.form.defaultRate')}
              value={v.cashAdvanceRate}
              onChange={(e) => set({ cashAdvanceRate: e.target.value })}
            />
          </Field>
          <Field id="cc-late" label={t('finance.credit.rates.late')}>
            <Input
              id="cc-late"
              inputMode="decimal"
              placeholder={t('finance.credit.form.defaultRate')}
              value={v.lateRate}
              onChange={(e) => set({ lateRate: e.target.value })}
            />
          </Field>
        </FormSection>
      </div>
    </Modal>
  )
}
